import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { testIds } from "@/constants/testIds";
import {
  Loader2,
  TrendingUp,
  Euro,
  Target,
  Trophy,
  Snowflake,
  Percent,
  FileText,
} from "lucide-react";

const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

export default function StatsView() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/stats?year=${year}&month=${month}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [year, month]);

  const monthKey = `${year}-${String(month).padStart(2, "0")}`;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-black uppercase tracking-tight text-white">
            I tuoi numeri
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Panoramica di incassi, richieste e conversion. Filtro per mese sugli incassi/commissioni; le classifiche sono cumulative.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid={testIds.adminStatsMonth}
            type="month"
            value={monthKey}
            onChange={(e) => {
              const [y, m] = e.target.value.split("-");
              setYear(Number(y));
              setMonth(Number(m));
            }}
            className="min-h-[42px] px-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
          />
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center py-12 text-zinc-500">
          <Loader2 className="animate-spin mr-2" size={18} /> Caricamento...
        </div>
      ) : (
        <>
          {/* KPI grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              icon={Euro}
              label={`Incasso ${MONTHS_IT[month - 1]}`}
              value={`€ ${data.month_revenue.toFixed(2)}`}
              hint="Preventivi confermati che coprono il mese"
              accent="emerald"
            />
            <KpiCard
              icon={Percent}
              label="Commissione mese"
              value={`€ ${data.commission_this_month.toFixed(2)}`}
              hint={`${data.commission_percent.toFixed(1)}% sul materiale partner`}
              accent="rose"
            />
            <KpiCard
              icon={Target}
              label="Conversion rate"
              value={`${data.conversion_rate.toFixed(1)}%`}
              hint={`${data.confirmed_quotes} confermati su ${data.total_quotes} totali`}
              accent="amber"
            />
            <KpiCard
              icon={TrendingUp}
              label="Preventivi in coda"
              value={data.new_quotes}
              hint={`${data.rejected_quotes} rifiutati totali`}
              accent="sky"
            />
          </div>

          {/* Top items */}
          <div className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={16} className="text-rose-500" />
              <h3 className="font-heading font-black uppercase text-white text-sm tracking-wider">
                Top 5 articoli richiesti
              </h3>
            </div>
            {data.top_items.length === 0 ? (
              <div className="text-xs text-zinc-500 py-4 text-center">
                Nessun preventivo ricevuto ancora.
              </div>
            ) : (
              <div className="space-y-1.5">
                {data.top_items.map((it, i) => (
                  <div
                    key={it.item_id}
                    className="flex items-center gap-3 py-1"
                  >
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center font-heading font-black text-xs shrink-0 ${
                        i === 0
                          ? "bg-rose-600 text-white"
                          : i === 1
                            ? "bg-rose-600/60 text-white"
                            : i === 2
                              ? "bg-rose-600/30 text-rose-200"
                              : "bg-zinc-800 text-zinc-400"
                      }`}
                    >
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">
                        {it.name}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                        richiesto {it.count} {it.count === 1 ? "volta" : "volte"}
                      </div>
                    </div>
                    <div className="font-mono font-bold text-emerald-400 text-sm shrink-0">
                      × {it.total_qty}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Slow items */}
          <div className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Snowflake size={16} className="text-sky-400" />
              <h3 className="font-heading font-black uppercase text-white text-sm tracking-wider">
                Articoli scarichi
              </h3>
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-auto">
                mai richiesti
              </span>
            </div>
            {data.slow_items.length === 0 ? (
              <div className="text-xs text-emerald-400 py-4 text-center font-mono uppercase tracking-widest">
                Ottimo — tutti gli articoli hanno ricevuto almeno una richiesta.
              </div>
            ) : (
              <div className="space-y-1.5">
                {data.slow_items.map((it) => (
                  <div
                    key={it.item_id}
                    className="flex items-center gap-3 py-1"
                  >
                    <div className="w-7 h-7 rounded-lg bg-sky-500/10 border border-sky-500/20 text-sky-400 flex items-center justify-center shrink-0">
                      <Snowflake size={12} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">
                        {it.name}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                        {it.category} · qty {it.quantity}
                      </div>
                    </div>
                    <div className="font-mono font-bold text-zinc-500 text-sm shrink-0">
                      € {it.daily_price.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, hint, accent = "rose" }) {
  const accentMap = {
    emerald: "bg-emerald-500/10 border-emerald-500/25 text-emerald-400",
    rose: "bg-rose-500/10 border-rose-500/25 text-rose-400",
    amber: "bg-amber-500/10 border-amber-500/25 text-amber-400",
    sky: "bg-sky-500/10 border-sky-500/25 text-sky-400",
  };
  return (
    <div className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-3 sm:p-4">
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 border ${accentMap[accent]}`}
      >
        <Icon size={16} />
      </div>
      <div className="font-mono font-black text-white text-lg sm:text-2xl leading-tight break-words">
        {value}
      </div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
        {label}
      </div>
      {hint && (
        <div className="text-[10px] text-zinc-600 mt-1 leading-snug">
          {hint}
        </div>
      )}
    </div>
  );
}
